import {
    type AgentRunMetrics,
    type ConversationalMMRunMetrics,
    type SproutRunMetrics,
    toThreadErrors,
    WidgetType,
} from '@fruitful/types';

import {
    isCMMBranch,
    isCMMStep,
    isRecord,
    toHandoff,
    toNullableIsoString,
    toNullableNumber,
} from './parsingHelpers';

const toBaseRunMetrics = (value: unknown): AgentRunMetrics | null => {
    if (!isRecord(value)) {
        return null;
    }

    const participantTurns = value.participantTurns;
    const agentTurns = value.agentTurns;

    if (
        typeof participantTurns !== 'number' ||
        typeof agentTurns !== 'number'
    ) {
        return null;
    }

    return {
        participantTurns,
        agentTurns,
        conversationDurationMs: toNullableNumber(value.conversationDurationMs),
        agentResponseWaitMs: toNullableNumber(value.agentResponseWaitMs),
        lastMessageAt: toNullableIsoString(value.lastMessageAt),
        errors: toThreadErrors(value.errors),
    };
};

export const parseConversationalMMRunMetrics = (
    value: unknown,
): ConversationalMMRunMetrics | null => {
    const baseMetrics = toBaseRunMetrics(value);
    if (baseMetrics == null || !isRecord(value)) {
        return null;
    }

    const rawAhaMoment = value.ahaMoment;
    const ahaMoment = isRecord(rawAhaMoment)
        ? {
              date:
                  typeof rawAhaMoment.date === 'string' ||
                  rawAhaMoment.date == null
                      ? (rawAhaMoment.date ?? null)
                      : null,
              headline:
                  typeof rawAhaMoment.headline === 'string' ||
                  rawAhaMoment.headline == null
                      ? (rawAhaMoment.headline ?? null)
                      : null,
              goalName:
                  typeof rawAhaMoment.goalName === 'string' ||
                  rawAhaMoment.goalName == null
                      ? (rawAhaMoment.goalName ?? null)
                      : null,
          }
        : null;

    return {
        ...baseMetrics,
        branch: isCMMBranch(value.branch) ? value.branch : null,
        currentStep: isCMMStep(value.currentStep) ? value.currentStep : null,
        stepsCompleted: toNullableNumber(value.stepsCompleted),
        stepsTotal: toNullableNumber(value.stepsTotal),
        ahaMoment,
        chatVersion:
            typeof value.chatVersion === 'string' ? value.chatVersion : null,
        artifactVersion:
            typeof value.artifactVersion === 'string'
                ? value.artifactVersion
                : null,
    };
};

const WIDGET_TYPE_VALUES = new Set<string>(Object.values(WidgetType));

const toWidgetsShown = (value: unknown): WidgetType[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(
        (widget): widget is WidgetType =>
            typeof widget === 'string' && WIDGET_TYPE_VALUES.has(widget),
    );
};

const toUniqueNonEmptyStrings = (...sources: unknown[]): string[] => {
    const seen = new Set<string>();
    const values: string[] = [];

    for (const source of sources) {
        if (!Array.isArray(source)) {
            continue;
        }

        for (const value of source) {
            if (typeof value !== 'string' || value === '' || seen.has(value)) {
                continue;
            }

            seen.add(value);
            values.push(value);
        }
    }

    return values;
};

export const SPROUT_STREAM_MULTIPLE_VALUES_LABEL = 'multiple values';

const lastValue = (values: readonly string[]): string | null =>
    values.length === 0 ? null : (values[values.length - 1] ?? null);

/** Unique models / efforts used on a thread. Accepts metrics or thread.metadata. */
export const collectSproutStreamSelectionValues = (
    value: unknown,
): {
    models: string[];
    reasoningEfforts: string[];
} => {
    if (!isRecord(value)) {
        return { models: [], reasoningEfforts: [] };
    }

    const nested = isRecord(value.metrics) ? value.metrics : null;

    return {
        models: toUniqueNonEmptyStrings(nested?.models, value.models),
        reasoningEfforts: toUniqueNonEmptyStrings(
            nested?.reasoningEfforts,
            value.reasoningEfforts,
        ),
    };
};

export const formatSproutStreamSelectionLabel = (
    values: readonly string[],
): string | null => {
    if (values.length === 0) {
        return null;
    }

    if (values.length === 1) {
        return values[0] ?? null;
    }

    return SPROUT_STREAM_MULTIPLE_VALUES_LABEL;
};

/** Most recently used model / reasoning effort (last unique array entry). */
export const getSproutStreamSelection = (
    value: unknown,
): {
    model: string | null;
    reasoningEffort: string | null;
} => {
    const { models, reasoningEfforts } =
        collectSproutStreamSelectionValues(value);

    return {
        model: lastValue(models),
        reasoningEffort: lastValue(reasoningEfforts),
    };
};

/** Grove label copy: the sole value, or "multiple values" when a thread mixed them. */
export const getSproutStreamSelectionLabels = (
    value: unknown,
): {
    model: string | null;
    reasoningEffort: string | null;
} => {
    const { models, reasoningEfforts } =
        collectSproutStreamSelectionValues(value);

    return {
        model: formatSproutStreamSelectionLabel(models),
        reasoningEffort: formatSproutStreamSelectionLabel(reasoningEfforts),
    };
};

export const parseSproutRunMetrics = (
    value: unknown,
): SproutRunMetrics | null => {
    const baseMetrics = toBaseRunMetrics(value);
    if (baseMetrics == null || !isRecord(value)) {
        return null;
    }

    return {
        ...baseMetrics,
        chartsShown:
            typeof value.chartsShown === 'number' ? value.chartsShown : 0,
        tablesShown:
            typeof value.tablesShown === 'number' ? value.tablesShown : 0,
        widgetsShown: toWidgetsShown(value.widgetsShown),
        handoff: toHandoff(value.handoff),
        offTopicFlags:
            typeof value.offTopicFlags === 'number' ? value.offTopicFlags : 0,
        ...collectSproutStreamSelectionValues(value),
    };
};
